import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { startTwoFactorSetupApi, confirmTwoFactorSetupApi } from '../../api/auth.api';
import { toLatinDigits } from '../../utils/nationalCode';

/**
 * فعال‌سازی ورود دو مرحله‌ای: اسکن QR → وارد کردن کد → ذخیره‌ی کدهای پشتیبان.
 * setupToken: هنگام «فعال‌سازی اجباری» در مرحله‌ی ورود (کاربر هنوز نشست ندارد)
 * onDone(data): پس از تأیید کاربر که کدهای پشتیبان را ذخیره کرده؛ data پاسخ سرور است
 * dark: ظاهر تیره برای صفحه‌ی ورود
 */
export default function TwoFactorSetupPanel({ setupToken, onDone, onCancel, dark = false }) {
  const [info, setInfo] = useState(null);
  const [qr, setQr] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState('');

  const text = dark ? 'text-white' : 'text-slate-800';
  const sub = dark ? 'text-white/60' : 'text-slate-500';
  const box = dark ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-slate-50';
  const input = dark
    ? 'border-white/10 bg-white/5 text-white placeholder:text-white/30 focus:border-emerald-400'
    : 'border-slate-200 bg-white text-slate-800 focus:border-emerald-500';

  useEffect(() => {
    let active = true;
    startTwoFactorSetupApi(setupToken)
      .then(async ({ data }) => {
        if (!active) return;
        const payload = data.data;
        setInfo(payload);
        setQr(await QRCode.toDataURL(payload.otpauthUrl, { margin: 1, width: 220 }));
      })
      .catch((err) => active && setError(err?.response?.data?.message || 'شروع فعال‌سازی ناموفق بود'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [setupToken]);

  const copy = async (value, key) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
    } catch {
      /* کپی در برخی مرورگرها مجاز نیست */
    }
  };

  const downloadCodes = () => {
    const blob = new Blob([`کدهای پشتیبان ورود دو مرحله‌ای\n\n${result.backupCodes.join('\n')}\n`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const { data } = await confirmTwoFactorSetupApi(toLatinDigits(code).replace(/\D/g, ''), setupToken);
      setResult(data.data);
    } catch (err) {
      setError(err?.response?.data?.message || 'تأیید کد ناموفق بود');
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------- مرحله‌ی آخر: کدهای پشتیبان ---------- */
  if (result) {
    return (
      <div className="space-y-4">
        <div>
          <h3 className={`text-lg font-black ${text}`}>✅ ورود دو مرحله‌ای فعال شد</h3>
          <p className={`mt-1 text-sm leading-7 ${sub}`}>
            این ۸ کد پشتیبان را همین حالا در جای امنی نگه دارید. اگر گوشی خود را گم کنید، هر کد فقط یک بار برای ورود قابل استفاده است.
            این کدها دوباره نمایش داده نمی‌شوند.
          </p>
        </div>

        <div dir="ltr" className={`grid grid-cols-2 gap-2 rounded-2xl border p-4 font-mono text-sm tracking-wider ${box} ${text}`}>
          {result.backupCodes.map((c) => (
            <span key={c}>{c}</span>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => copy(result.backupCodes.join('\n'), 'codes')} className="rounded-xl bg-emerald-500 px-4 py-2 text-xs font-black text-white hover:bg-emerald-600">
            {copied === 'codes' ? 'کپی شد ✓' : 'کپی کدها'}
          </button>
          <button type="button" onClick={downloadCodes} className={`rounded-xl border px-4 py-2 text-xs font-black ${box} ${text}`}>
            دانلود فایل متنی
          </button>
        </div>

        <label className={`flex cursor-pointer items-center gap-2 text-sm ${sub}`}>
          <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} className="h-4 w-4" />
          کدهای پشتیبان را ذخیره کردم
        </label>

        <button
          type="button"
          disabled={!saved}
          onClick={() => onDone(result)}
          className="h-12 w-full rounded-full bg-emerald-500 font-black text-white transition hover:bg-emerald-600 disabled:opacity-40"
        >
          ادامه
        </button>
      </div>
    );
  }

  /* ---------- مرحله‌ی اسکن و تأیید ---------- */
  return (
    <form onSubmit={handleConfirm} className="space-y-4">
      <div>
        <h3 className={`text-lg font-black ${text}`}>فعال‌سازی ورود دو مرحله‌ای</h3>
        <p className={`mt-1 text-sm leading-7 ${sub}`}>
          ۱) یک برنامه‌ی احراز هویت (Google Authenticator، Microsoft Authenticator، Authy یا 2FAS) را نصب کنید.
          ۲) کد QR را اسکن کنید. ۳) کد ۶ رقمی نمایش‌داده‌شده را وارد کنید.
        </p>
      </div>

      {loading ? (
        <div className={`py-8 text-center text-sm ${sub}`}>در حال آماده‌سازی...</div>
      ) : info ? (
        <>
          <div className={`flex flex-col items-center gap-3 rounded-2xl border p-4 ${box}`}>
            {qr && <img src={qr} alt="QR" className="h-44 w-44 rounded-xl bg-white p-2" />}
            <p className={`text-xs ${sub}`}>اگر نمی‌توانید اسکن کنید، این کلید را دستی وارد کنید:</p>
            <button
              type="button"
              dir="ltr"
              onClick={() => copy(info.secret, 'secret')}
              className={`rounded-xl border px-3 py-2 font-mono text-xs tracking-widest ${box} ${text}`}
              title="کلیک برای کپی"
            >
              {info.secret} {copied === 'secret' ? '✓' : ''}
            </button>
          </div>

          <input
            dir="ltr"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={7}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="کد ۶ رقمی"
            className={`h-12 w-full rounded-2xl border-2 text-center font-mono text-xl tracking-[0.5em] outline-none transition ${input}`}
          />
        </>
      ) : null}

      {error && <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-3 text-sm font-bold text-red-400">{error}</div>}

      <button
        type="submit"
        disabled={submitting || !info || toLatinDigits(code).replace(/\D/g, '').length < 6}
        className="h-12 w-full rounded-full bg-emerald-500 font-black text-white transition hover:bg-emerald-600 disabled:opacity-40"
      >
        {submitting ? 'در حال بررسی...' : 'تأیید و فعال‌سازی'}
      </button>

      {onCancel && (
        <button type="button" onClick={onCancel} className={`w-full text-center text-xs font-bold ${sub} hover:underline`}>
          انصراف
        </button>
      )}
    </form>
  );
}
